import React, { useState } from 'react';
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Badge,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';

const featureNames = {
  step_by_step_view: 'Action Plan',
  calendar_view: 'Project Timeline',
  sow_view: 'Statement of Work',
  readme_view: 'AI Prompt Generator',
  product_strategy_view: 'Product Strategy',
  competitor_analysis_view: 'Competitor Analysis',
};

// Features that are document/view only (no programs added)
const documentOnlyFeatures = ['sow_view', 'calendar_view', 'readme_view', 'product_strategy_view', 'competitor_analysis_view'];

// Features that involve programs/tasks
const programBasedFeatures = ['step_by_step_view'];

export default function FeatureEnablementConfirmation({
  featureId,
  planId,
  programsCount = 0,
  stagesCount = 0,
  toolsCount = 0,
  onConfirm = null,
  onCancel = null,
  finalProgramSequence = null,
  selectedPrograms = null,
  handleComponentAction = null,
  triggerWorkflow = false,
  isDocumentOnly = false,
}) {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  const handleConfirm = async () => {
    if (!planId) {
      setError('No plan ID provided');
      return;
    }

    if (!handleComponentAction) {
      setError('No action handler available');
      console.error('❌ FeatureEnablementConfirmation: handleComponentAction not provided');
      return;
    }

    setIsConfirming(true);
    setError(null);
    
    try {
      console.log(`🚀 Enabling feature ${featureId} for plan ${planId} via ComponentActionHandler`);

      const actionData = {
        featureId,
        programs: isDocumentOnly ? [] : (finalProgramSequence || []),
        triggerWorkflow: triggerWorkflow,
      };

      console.log(`📦 Action data:`, {
        featureId,
        programsCount: actionData.programs.length,
        triggerWorkflow,
        isDocumentOnly,
      });

      const result = await handleComponentAction('enable_feature', 'FeatureEnablementConfirmation', actionData);

      if (result && result.success) {
        console.log(`✅ Feature ${featureId} enabled successfully via capability:`, result);
        setIsEnabled(true);

        toast({
          title: 'Success!',
          description: `${featureNames[featureId] || featureId} has been enabled.`,
          status: 'success',
          duration: 3000,
        });

        window.dispatchEvent(new CustomEvent('planDataUpdated', {
          detail: { featureEnabled: featureId, planId }
        }));

        if (onConfirm) {
          onConfirm({
            featureId,
            planId,
            programsCount: result.programsAdded || programsCount,
            stagesCount,
            toolsCount,
          });
        }
      } else {
        throw new Error(result?.message || 'Failed to enable feature');
      }
    } catch (err) {
      console.error(`❌ Error enabling feature:`, err);
      setError(err.message || 'Failed to enable feature');
      setIsConfirming(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  if (isEnabled) {
    return (
      <Box
        mt={2}
        p={3}
        bg="white"
        borderRadius="md"
        border="1px solid"
        borderColor="green.300"
        width="100%"
      >
        <HStack spacing={2}>
          <CheckIcon color="green.500" />
          <Text fontSize="sm" color="green.700" fontWeight="bold">
            Change Completed:
          </Text>
          <Badge colorScheme="green" variant="outline">
            {featureNames[featureId] || featureId}
          </Badge>
        </HStack>
        <Text fontSize="sm" color="gray.600" mt={2}>
          {documentOnlyFeatures.includes(featureId) 
            ? 'Feature enabled and ready to use.'
            : `${programsCount} program${programsCount !== 1 ? 's' : ''} with ${toolsCount} task${toolsCount !== 1 ? 's' : ''} added to your plan.`
          }
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        mt={2}
        p={3}
        bg="white"
        borderRadius="md"
        border="1px solid"
        borderColor="red.300"
        width="100%"
      >
        <VStack align="start" spacing={2}>
          <HStack spacing={2}>
            <Text color="red.500">❌</Text>
            <Text fontSize="sm" color="red.700" fontWeight="bold">
              Error:
            </Text>
            <Badge colorScheme="red" variant="outline">
              {featureNames[featureId] || featureId}
            </Badge>
          </HStack>
          <Text fontSize="sm" color="red.600">{error}</Text>
          <HStack spacing={2}>
            <Button
              size="xs"
              variant="outline"
              colorScheme="gray"
              onClick={handleCancel}
            >
              Dismiss
            </Button>
            <Button
              size="xs"
              colorScheme="brand"
              onClick={handleConfirm}
            >
              Retry
            </Button>
          </HStack>
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      mt={2}
      p={3}
      bg="white"
      borderRadius="md"
      border="1px solid"
      borderColor="brand.300"
      width="100%"
    >
      <VStack align="start" spacing={3}>
        <HStack spacing={2}>
          <Text fontSize="sm" color="gray.700" fontWeight="bold">
            Enable Feature:
          </Text>
          <Badge colorScheme="brand" variant="outline">
            {featureNames[featureId] || featureId}
          </Badge>
        </HStack>

        <Text fontSize="sm" color="gray.600">
          {documentOnlyFeatures.includes(featureId)
            ? `This will enable the ${featureNames[featureId] || featureId} feature for your plan.`
            : `This will add ${programsCount} program${programsCount !== 1 ? 's' : ''} with ${toolsCount} task${toolsCount !== 1 ? 's' : ''} to your plan.`
          }
        </Text>

        <HStack spacing={2} width="100%" justify="flex-start">
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={handleCancel}
            isDisabled={isConfirming}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            colorScheme="brand"
            onClick={handleConfirm}
            isLoading={isConfirming}
            loadingText="Enabling..."
          >
            Enable
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
